/**
 * Direct browser → Anthropic API client.
 *
 * The user's API key is passed in on every call and is never stored or
 * transmitted anywhere other than `api.anthropic.com`. Callers are expected to
 * hold the key in component state (never in storage) and pass it through.
 */

const ANTHROPIC_API_BASE = 'https://api.anthropic.com';
const ANTHROPIC_VERSION = '2023-06-01';
const DEFAULT_MAX_TOKENS = 8192;

const BROWSER_ACCESS_HEADERS = {
  'anthropic-version': ANTHROPIC_VERSION,
  'anthropic-dangerous-direct-browser-access': 'true',
} as const;

export interface AnthropicModel {
  id: string;
  displayName: string;
  createdAt: string;
}

export class AnthropicApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AnthropicApiError';
    this.status = status;
  }
}

function authHeaders(apiKey: string): HeadersInit {
  return {
    ...BROWSER_ACCESS_HEADERS,
    'x-api-key': apiKey,
  };
}

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return body?.error?.message ?? `Anthropic API returned ${response.status}`;
  } catch {
    return `Anthropic API returned ${response.status}`;
  }
}

/**
 * List models available to the supplied key; used for key validation and
 * populating the list of models the user can choose from.
 */
export async function fetchAnthropicModels(apiKey: string): Promise<AnthropicModel[]> {
  const response = await fetch(`${ANTHROPIC_API_BASE}/v1/models`, {
    method: 'GET',
    headers: authHeaders(apiKey),
  });

  if (!response.ok) {
    throw new AnthropicApiError(await extractErrorMessage(response), response.status);
  }

  const body = await response.json();
  const data: Array<{ id: string; display_name: string; created_at: string }> = body?.data ?? [];

  return data
    .map(({ id, display_name, created_at }) => ({
      id,
      displayName: display_name,
      createdAt: created_at,
    }))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * Stream a completion from Anthropic's Messages API.
 *
 * Yields the text deltas as they arrive. Throws AnthropicApiError on a non-2xx
 * response and propagates errors emitted mid-stream via SSE `error` events.
 */
export async function* streamAnthropicResponse(
  prompt: string,
  modelId: string,
  apiKey: string,
  options: { maxTokens?: number; signal?: AbortSignal } = {},
): AsyncGenerator<string, void, void> {
  const response = await fetch(`${ANTHROPIC_API_BASE}/v1/messages`, {
    method: 'POST',
    headers: {
      ...authHeaders(apiKey),
      'content-type': 'application/json',
      accept: 'text/event-stream',
    },
    body: JSON.stringify({
      model: modelId,
      max_tokens: options.maxTokens ?? DEFAULT_MAX_TOKENS,
      stream: true,
      messages: [{ role: 'user', content: prompt }],
    }),
    signal: options.signal,
  });

  if (!response.ok) {
    throw new AnthropicApiError(await extractErrorMessage(response), response.status);
  }
  if (!response.body) {
    throw new AnthropicApiError('Anthropic API returned no response body', response.status);
  }

  for await (const event of parseSseStream(response.body)) {
    if (event.event === 'content_block_delta') {
      const delta = event.data?.delta;
      if (delta?.type === 'text_delta' && typeof delta.text === 'string') {
        yield delta.text;
      }
    } else if (event.event === 'error') {
      const message = event.data?.error?.message ?? 'Anthropic stream error';
      throw new AnthropicApiError(message, response.status);
    } else if (event.event === 'message_stop') {
      return;
    }
  }
}

interface SseEvent {
  event: string;
  data: any;
}

/**
 * Parse a Server-Sent Events stream into discrete events. SSE frames are
 * separated by blank lines; each frame may carry an `event:` type and one or
 * more `data:` lines that we JSON-decode together.
 */
async function* parseSseStream(stream: ReadableStream<Uint8Array>): AsyncGenerator<SseEvent, void, void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let separatorIndex: number;
      while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawFrame = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);

        const parsed = parseSseFrame(rawFrame);
        if (parsed) yield parsed;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSseFrame(frame: string): SseEvent | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of frame.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const colonIndex = line.indexOf(':');
    const field = colonIndex === -1 ? line : line.slice(0, colonIndex);
    const value = colonIndex === -1 ? '' : line.slice(colonIndex + 1).replace(/^ /, '');
    if (field === 'event') {
      event = value;
    } else if (field === 'data') {
      dataLines.push(value);
    }
  }

  if (dataLines.length === 0) return null;

  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}
